import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Link } from "react-router-dom";
import BookCover from "@/components/landing/BookCover";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const line = (i) => ({
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.1, delay: 0.35 + i * 0.13, ease: EASE } },
});

const fade = (d) => ({
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 1, delay: d, ease: EASE } },
});

export default function Hero() {
  const stageRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 16 });

  const { scrollY } = useScroll();
  const booksY = useTransform(scrollY, [0, 900], [0, 90]);
  const textY = useTransform(scrollY, [0, 900], [0, -60]);

  const onMove = (e) => {
    const r = stageRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section className="hero" data-testid="hero-section">
      <div className="container hero-grid">
        <motion.div style={{ y: textY }} initial="hidden" animate="show" className="hero-copy">
          <motion.p className="hero-eyebrow" variants={fade(0.25)} data-testid="hero-eyebrow">
            Guides numériques — livraison instantanée
          </motion.p>
          <h1 className="hero-title" data-testid="hero-title">
            <span className="line-mask"><motion.span variants={line(0)}>Mémorise le Coran</motion.span></span>
            <span className="line-mask"><motion.span variants={line(1)}>avec <em className="accent">un plan clair</em>,</motion.span></span>
            <span className="line-mask"><motion.span variants={line(2)}>jour après jour.</motion.span></span>
          </h1>
          <motion.p className="hero-sub" variants={fade(0.9)} data-testid="hero-subtitle">
            Deux guides numériques pour structurer ta mémorisation sur 6 mois et ta
            révision sur 30 jours — sans te sentir dépassé.
          </motion.p>
          <motion.div className="cta-row" variants={fade(1.05)}>
            <Link to="/guides" className="btn" data-testid="hero-cta-guides">
              Voir les guides
            </Link>
            <button className="btn secondary" onClick={() => scrollToSection("#methode")} data-testid="hero-cta-methode">
              Comment ça marche
            </button>
          </motion.div>
          <motion.div className="stat-row" variants={fade(1.2)} data-testid="hero-stats">
            <div className="stat"><b>6 mois</b><span>Plan de mémorisation</span></div>
            <div className="stat"><b>30 jours</b><span>Plan de révision</span></div>
            <div className="stat"><b>100%</b><span>Livraison instantanée</span></div>
          </motion.div>
        </motion.div>

        <motion.div
          ref={stageRef}
          className="book-stage"
          style={{ y: booksY }}
          onMouseMove={onMove}
          onMouseLeave={() => { mx.set(0); my.set(0); }}
          initial={{ opacity: 0, x: 90 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.4, delay: 0.55, ease: EASE }}
          data-testid="hero-books"
        >
          <div className="spotlight" aria-hidden="true" />
          <div className="book-tilt tilt-back">
            <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
              <motion.div animate={{ y: [0, -9, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}>
                <BookCover variant="rev" />
              </motion.div>
            </motion.div>
          </div>
          <div className="book-tilt tilt-front">
            <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
              <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
                <BookCover variant="mem" />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
